export { showApiError, catalogActionButton } from "./showApiError";
export type { ShowApiErrorOptions } from "./showApiError";
export {
  resolveFriendlyError,
  whatsappAccessLostMessage,
  whatsappAccessLostError,
  type FriendlyError,
  type ApiErrorLike,
} from "./errorCatalog";

import {
  resolveFriendlyError,
  whatsappAccessLostError,
  whatsappAccessLostMessage,
  type FriendlyError,
} from "./errorCatalog";
import { audienceOf, getCurrentUser } from "@/lib/auth/currentUser";

export function friendlyMessage(
  input: unknown,
  fallback?: string,
): string {
  return resolveFriendlyError(
    input as Parameters<typeof resolveFriendlyError>[0],
    fallback ? { description: fallback } : undefined,
    audienceOf(getCurrentUser()),
  ).description;
}

/*
 * Stored sync errors are written by the backend as "Meta API #<code>: <msg>".
 * Rewrite to "<msg> (#<code>)" so the catalog's code extractor can read it.
 */
export function friendlyStoredMetaError(text: string | null | undefined, fallback?: string): string {
  const raw = String(text || "");
  if (!raw) return fallback ?? raw;
  const m = raw.match(/^\s*Meta API #(\d+):\s*([\s\S]*)$/);
  return friendlyMessage(m ? `${m[2]} (#${m[1]})` : raw, fallback);
}

export function isFailureNotification(type: unknown): boolean {
  const t = String(type || "").toLowerCase();
  return t === "error" || t.endsWith("_failed") || t.includes("failure");
}

/*
 * Background jobs (template/catalog sync, scheduled posts and messages) put
 * the provider's raw error — often Meta's Graph API text — in the
 * notification message. Failure rows go through the catalog so the bell and
 * its toast show the friendly copy; plain prose passes through unchanged.
 */
export function friendlyNotificationMessage(n: {
  type?: unknown;
  message?: string | null;
  data?: unknown;
}): string {
  const message = n.message || "";
  if (!message) return message;
  const data = n.data && typeof n.data === "object" ? (n.data as Record<string, unknown>) : null;

  if (String(data?.kind || "").startsWith("whatsapp_")) {
    const accessLost = whatsappAccessLostMessage(message);
    if (accessLost) return accessLost;
  }

  const errorCode = typeof data?.code === "string" ? data.code : undefined;
  if (!isFailureNotification(n.type) && !errorCode && !data?.error) return message;
  return friendlyMessage({ code: errorCode, error: message });
}

// The catalog's fix-it link for a failure notification, read from its RAW
// message (the friendly text no longer carries Meta's code or phrasing).
export function notificationFixAction(n: {
  type?: unknown;
  title?: string | null;
  message?: string | null;
  data?: unknown;
}): FriendlyError["action"] {
  const message = n.message || "";
  if (!message) return undefined;
  const data = n.data && typeof n.data === "object" ? (n.data as Record<string, unknown>) : null;
  const audience = audienceOf(getCurrentUser());

  if (String(data?.kind || "").startsWith("whatsapp_")) {
    if (whatsappAccessLostError(message)) return resolveFriendlyError({ code: "WHATSAPP_ACCESS_LOST" }, undefined, audience).action;
  }

  const errorCode = typeof data?.code === "string" ? data.code : undefined;
  if (!isFailureNotification(n.type) && !errorCode && !data?.error) return undefined;
  // The title often names the integration ("Shopify Checkouts Sync Failed")
  // when the provider's message doesn't.
  const context = [n.title, data?.provider, data?.kind].filter(Boolean).join(" ");
  return resolveFriendlyError({ code: errorCode, error: message }, undefined, audience, context).action;
}
